import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../lib/cn';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium',
    'transition-colors duration-hover ease-out',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-app',
    'disabled:pointer-events-none disabled:opacity-50',
    'active:scale-[0.995]',
  ].join(' '),
  {
    variants: {
      variant: {
        primary:
          'bg-[var(--action-bg)] text-[var(--action-text)] hover:bg-[var(--action-hover)] [&_svg]:text-current',
        secondary:
          'bg-bg-surface text-text-primary border border-border hover:border-border-strong hover:bg-bg-elevated',
        ghost:
          'bg-transparent text-text-primary hover:bg-bg-elevated',
        danger:
          'bg-danger text-white hover:bg-danger/90 [&_svg]:text-white',
        link:
          'bg-transparent text-accent-500 hover:underline underline-offset-4 h-auto p-0',
      },
      size: {
        sm: 'h-7 px-3 text-caption',
        md: 'h-[var(--control-height)] px-4 text-body',
        lg: 'h-11 px-5 text-body-lg',
        icon: 'h-9 w-9 p-0',
      },
    },
    compoundVariants: [
      { variant: 'link', size: 'sm', class: 'h-auto px-0' },
      { variant: 'link', size: 'md', class: 'h-auto px-0' },
      { variant: 'link', size: 'lg', class: 'h-auto px-0' },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const isDisabled = disabled || loading;
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            <span className="sr-only">Loading</span>
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
